import { useMemo } from 'react'
import type { RangeKey } from '@dataTypes/dashboard.types'
import {
  getARAging,
  getARAgingOverview,
} from '@services/api/dashboard/finance.api'
import type {
  ARAgingDashboard,
  PeriodComparisonMetric,
} from '@services/api/dashboard/finance.api'
import {
  buildComparisonFooter,
  computeChartAxis,
  formatCurrency,
  formatGoalCurrencyLabel,
  HYGIENE_PLACEHOLDER,
} from '@utils/dashboardFinance.util'
import type { DashboardMetricItemData } from '@utils/dashboardMetrics.util'
import { hasNonZeroDashboardValues } from '@utils/dashboardSectionEmpty.util'
import {
  useDashboardPeriodFetch,
  useDashboardPeriodParams,
  type DashboardPeriodFetcher,
} from './useDashboardPeriod'
import { useClinicGoal } from './useClinicGoal'

export type UseARAgingOptions = {
  range: RangeKey
  date: string
  enabled?: boolean
  queued?: boolean
}

type ARAgingChartRow = {
  label: string
  insurance: number
  patient: number
}

export type ARAgingDetailBucketTableRow = {
  bucket: '0-30' | '31-60' | '61-90' | '90+'
  insuranceAR: number
  patientAR: number
  percentOfOverdueAR: number
  appointmentCount: number
  balance: number
}

export type UseARAgingResult = {
  loading: boolean
  error: string | null
  hasData: boolean
  /** True once the response has arrived (independent of meaningful content). */
  hasPayload: boolean
  totals: ARAgingDashboard['totals'] | undefined
  arType: ARAgingDashboard['arType']
  chartData: ARAgingChartRow[]
  arValueAxis: ReturnType<typeof computeChartAxis>
  bucketTableRows: ARAgingDetailBucketTableRow[]
  arAgingBalanceGoalLabel: string
  cardMetrics: DashboardMetricItemData[]
}

function buildCardMetric(
  id: string,
  leftLabel: string,
  metric: PeriodComparisonMetric,
): DashboardMetricItemData {
  const { up, text } = buildComparisonFooter(metric, { isCurrency: true })
  return {
    id,
    leftLabel,
    value: formatCurrency(metric.current, { compact: true }),
    footer: text,
    footerTrend: up ? 'up' : 'down',
  }
}

function buildCardMetrics(
  totals: ARAgingDashboard['totals'] | undefined,
  loading: boolean,
): DashboardMetricItemData[] {
  if (loading || !totals) {
    return [
      { id: 'ar-total', leftLabel: 'Total AR', value: HYGIENE_PLACEHOLDER },
      {
        id: 'ar-insurance',
        leftLabel: 'Insurance AR',
        value: HYGIENE_PLACEHOLDER,
      },
      {
        id: 'ar-patient',
        leftLabel: 'Patient AR',
        value: HYGIENE_PLACEHOLDER,
      },
      {
        id: 'ar-overdue',
        leftLabel: 'Overdue AR',
        value: HYGIENE_PLACEHOLDER,
      },
    ]
  }
  return [
    buildCardMetric('ar-total', 'Total AR', totals.totalAR),
    buildCardMetric('ar-insurance', 'Insurance AR', totals.insuranceAR),
    buildCardMetric('ar-patient', 'Patient AR', totals.patientAR),
    buildCardMetric('ar-overdue', 'Overdue AR', totals.overdueAR),
  ]
}

function useARAgingFromFetcher({
  range,
  date,
  enabled = true,
  queued = false,
  fetcher,
}: UseARAgingOptions & {
  fetcher: DashboardPeriodFetcher<Awaited<ReturnType<typeof getARAging>>>
}): UseARAgingResult {
  const queryParams = useDashboardPeriodParams(range, date)
  const { data, loading, error } = useDashboardPeriodFetch(
    queryParams,
    fetcher,
    { enabled, queued },
  )
  const { goal: clinicGoal } = useClinicGoal({ range, date, enabled, queued })
  const arAgingBalanceGoalLabel = formatGoalCurrencyLabel(
    clinicGoal?.ar_aging_balance_goal,
  )

  const chartData: ARAgingChartRow[] = useMemo(
    () =>
      (data?.buckets ?? []).map((b) => ({
        label: b.bucket,
        insurance: b.insuranceAR,
        patient: b.patientAR,
      })),
    [data],
  )

  const arValueAxis = useMemo(() => {
    const maxVal = chartData.reduce(
      (m, p) => Math.max(m, p.insurance + p.patient),
      0,
    )
    return computeChartAxis([maxVal])
  }, [chartData])

  const bucketTableRows: ARAgingDetailBucketTableRow[] = useMemo(
    () =>
      (data?.buckets ?? []).map((b) => ({
        ...b,
        balance: b.insuranceAR + b.patientAR,
      })),
    [data],
  )

  const cardMetrics = useMemo(
    () => buildCardMetrics(data?.totals, loading),
    [data?.totals, loading],
  )

  const hasData = useMemo(() => {
    if (data == null) return false
    return (
      hasNonZeroDashboardValues(
        data.totals.totalAR.current,
        data.totals.insuranceAR.current,
        data.totals.patientAR.current,
        data.totals.overdueAR.current,
      ) ||
      data.buckets.some((bucket) =>
        hasNonZeroDashboardValues(bucket.insuranceAR, bucket.patientAR),
      )
    )
  }, [data])

  return {
    loading,
    error,
    hasData,
    hasPayload: data != null,
    totals: data?.totals,
    arType: data?.arType ?? [],
    chartData,
    arValueAxis,
    bucketTableRows,
    arAgingBalanceGoalLabel,
    cardMetrics,
  }
}

export function useARAging(options: UseARAgingOptions): UseARAgingResult {
  return useARAgingFromFetcher({ ...options, fetcher: getARAging })
}

export function useARAgingOverview(
  options: UseARAgingOptions,
): UseARAgingResult {
  return useARAgingFromFetcher({ ...options, fetcher: getARAgingOverview })
}
